import { useState } from "react";

import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import CloseIcon from "@mui/icons-material/Close";
import FilterListIcon from "@mui/icons-material/FilterList";
import {
  Badge,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import { usePostHog } from "posthog-js/react";

import { CarrierQuoteList } from "components/Carrier/components/CarrierQuoteList/CarrierQuoteList";
import { QuickShipmentsBrowser } from "components/Carrier/components/QuickShipmentsBrowser";
import { QuoteFilters } from "components/Carrier/components/QuoteFilters";
import { QuoteFiltersMorePanel } from "components/Carrier/components/QuoteFiltersMorePanel";
import { DashboardBackButton } from "components/Shipment/components/DashboardBackButton";
import { ClientAppEvent } from "constants/index";
import {
  useCarrierQuoteFilterHandlers,
  useCarrierQuotes,
  useResettablePage,
  useShipmentFiltersContext,
} from "hooks";
import {
  getActiveFiltersCount,
  getActiveQuoteFiltersCount,
  getPageCount,
} from "utils";

import { ShipmentSidebarViewDropdown } from "../ShipmentsFilter/components/ShipmentSidebarViewDropdown";

import type { ShipmentSidebarView } from "../ShipmentsFilter/components/ShipmentSidebarViewDropdown";

interface Props {
  shipments: CarrierCondensedShipment[];
  shipmentSelectionIsDisabled: boolean;
  handlePageSelected: (e: React.ChangeEvent<unknown>, value: number) => void;
  page: number;
  pages: number;
  shipmentCount: number;
  showDashboardBackButton?: boolean;
  onFiltersClick?: VoidFunction;
  onCollapse?: VoidFunction;
}

const SIDEBAR_QUOTES_PAGE_SIZE = 5;

export function ShipmentSideBar(props: Props) {
  const {
    shipments,
    shipmentSelectionIsDisabled,
    handlePageSelected,
    page,
    pages,
    shipmentCount,
    showDashboardBackButton = true,
    onFiltersClick,
    onCollapse,
  } = props;
  const { filters, updateShipmentRelation } = useShipmentFiltersContext();
  const posthog = usePostHog();

  const [isViewingQuotes, setIsViewingQuotes] = useState(false);
  const [quoteFiltersOpen, setQuoteFiltersOpen] = useState(false);
  const [quoteFiltersMorePanelOpen, setQuoteFiltersMorePanelOpen] =
    useState(true);

  const {
    filters: quoteFilters,
    filterClearCount,
    handleShipmentNameChange,
    handleQuoteStatusChange,
    handleSortByChange,
    handleEquipmentTypesChange,
    handleServiceTypesChange,
    handleShipmentTypeChange,
    handleOriginProvincesChange,
    handleDestinationProvincesChange,
    handlePickupDateAfterChange,
    handlePickupDateBeforeChange,
    handleDeliveryDateAfterChange,
    handleDeliveryDateBeforeChange,
    handleShipmentStatusChange,
    handleClearFilters,
  } = useCarrierQuoteFilterHandlers({ surface: "shipment_detail_sidebar" });

  const [quotePage, setQuotePage] = useResettablePage(quoteFilters);

  const {
    quotes,
    loading: quotesLoading,
    error: quotesError,
    count: quoteCount,
  } = useCarrierQuotes({
    currentPage: quotePage,
    pageSize: SIDEBAR_QUOTES_PAGE_SIZE,
    filters: quoteFilters,
    enabled: isViewingQuotes,
  });
  const quotePages = getPageCount(quoteCount ?? 0, SIDEBAR_QUOTES_PAGE_SIZE);

  const handleShipmentRelationChange = (value: ShipmentRelation) => {
    setIsViewingQuotes(false);
    updateShipmentRelation(value);
  };

  const handleQuotesSelected = () => {
    setIsViewingQuotes(true);
    posthog?.capture(
      ClientAppEvent.CARRIER_SHIPMENT_DETAIL_SIDEBAR_VIEW_CHANGED,
      { view: "quotes" },
    );
  };

  const getShipmentsAvailableCopy = () => {
    if (isViewingQuotes) {
      return "My Quotes";
    }
    if (filters && filters?.shipmentRelationship === "partner") {
      return "My Partner Shipments";
    }
    return "Public Shipments";
  };

  const activeFiltersCount = isViewingQuotes
    ? getActiveQuoteFiltersCount(quoteFilters)
    : getActiveFiltersCount(filters);

  const sidebarView: ShipmentSidebarView = isViewingQuotes
    ? "quotes"
    : (filters?.shipmentRelationship ?? "public");

  const handleQuoteFiltersClose = () => {
    setQuoteFiltersOpen(false);
  };

  const handleQuoteFiltersMoreToggle = () => {
    posthog?.capture(ClientAppEvent.CARRIER_MY_QUOTES_MORE_FILTERS_TOGGLED, {
      opened: !quoteFiltersMorePanelOpen,
      surface: "shipment_detail_sidebar",
    });
    setQuoteFiltersMorePanelOpen((prev) => !prev);
  };

  const handleFiltersButtonClick = () => {
    if (isViewingQuotes) {
      setQuoteFiltersOpen(true);
      posthog?.capture(
        ClientAppEvent.CARRIER_SHIPMENT_DETAIL_QUOTE_FILTER_BUTTON_CLICKED,
        { active_filters_count: activeFiltersCount },
      );
    } else {
      onFiltersClick?.();
    }
  };

  return (
    <Stack
      direction="column"
      spacing={0.5}
      sx={{
        pr: ".7rem",
      }}
    >
      {showDashboardBackButton && <DashboardBackButton />}
      <Stack direction="row" spacing={1} alignItems="center">
        <ShipmentSidebarViewDropdown
          value={sidebarView}
          onShipmentRelationChange={handleShipmentRelationChange}
          onQuotesSelected={handleQuotesSelected}
        />
        {(Boolean(onFiltersClick) || isViewingQuotes) && (
          <Badge
            badgeContent={activeFiltersCount}
            invisible={activeFiltersCount === 0}
            sx={{
              "& .MuiBadge-badge": {
                backgroundColor: "#9c27b0",
                color: "white",
                fontSize: "0.75rem",
                minWidth: "18px",
                height: "18px",
                borderRadius: "9px",
              },
            }}
          >
            <Tooltip title="Filters">
              <IconButton
                onClick={handleFiltersButtonClick}
                size="small"
                sx={{
                  color: "primary.main",
                  border: "1px solid",
                  borderColor: "primary.main",
                  borderRadius: 1,
                  width: 32,
                  height: 48,
                  "&:hover": {
                    backgroundColor: "primary.main",
                    color: "white",
                  },
                }}
              >
                <FilterListIcon />
              </IconButton>
            </Tooltip>
          </Badge>
        )}
      </Stack>
      <Stack direction="row" alignItems="center" justifyContent="space-between">
        <Typography variant="h6">
          {getShipmentsAvailableCopy()} (
          {(isViewingQuotes ? quoteCount : shipmentCount) ?? "-"})
        </Typography>
        {onCollapse && (
          <Tooltip title="Hide shipment list">
            <IconButton
              onClick={onCollapse}
              size="small"
              sx={{
                border: "1px solid",
                borderColor: "grey.300",
                borderRadius: "4px",
                "&:hover": {
                  backgroundColor: "grey.100",
                },
              }}
            >
              <ChevronLeftIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
      </Stack>
      {isViewingQuotes ? (
        <CarrierQuoteList
          quotes={quotes}
          loading={quotesLoading}
          error={quotesError}
          currentPage={quotePage}
          pageCount={quotePages}
          onPageSelected={setQuotePage}
        />
      ) : (
        <QuickShipmentsBrowser
          shipments={shipments}
          shipmentSelectionIsDisabled={shipmentSelectionIsDisabled}
          page={page}
          pages={pages}
          handlePageSelected={handlePageSelected}
        />
      )}
      <Dialog
        open={quoteFiltersOpen}
        onClose={handleQuoteFiltersClose}
        maxWidth="sm"
        fullWidth
        aria-label="My Quotes filters"
      >
        <DialogTitle
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          My Quotes
          <Tooltip title="Close">
            <IconButton onClick={handleQuoteFiltersClose} size="small">
              <CloseIcon />
            </IconButton>
          </Tooltip>
        </DialogTitle>
        <DialogContent sx={{ "&&": { pt: 3 } }}>
          <Stack rowGap={2}>
            <QuoteFilters
              filters={quoteFilters}
              onShipmentNameChange={handleShipmentNameChange}
              onQuoteStatusChange={handleQuoteStatusChange}
              onSortByChange={handleSortByChange}
              morePanelOpen={quoteFiltersMorePanelOpen}
              onMoreToggle={handleQuoteFiltersMoreToggle}
              filterClearCount={filterClearCount}
            />
            {quoteFiltersMorePanelOpen && (
              <QuoteFiltersMorePanel
                filters={quoteFilters}
                onEquipmentTypesChange={handleEquipmentTypesChange}
                onServiceTypesChange={handleServiceTypesChange}
                onShipmentTypeChange={handleShipmentTypeChange}
                onOriginProvincesChange={handleOriginProvincesChange}
                onDestinationProvincesChange={handleDestinationProvincesChange}
                onPickupDateAfterChange={handlePickupDateAfterChange}
                onPickupDateBeforeChange={handlePickupDateBeforeChange}
                onDeliveryDateAfterChange={handleDeliveryDateAfterChange}
                onDeliveryDateBeforeChange={handleDeliveryDateBeforeChange}
                onShipmentStatusChange={handleShipmentStatusChange}
                onClearFilters={handleClearFilters}
                showResetButton={false}
              />
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button
            variant="outlined"
            color="error"
            startIcon={<CloseIcon />}
            onClick={handleClearFilters}
          >
            Clear Filters
          </Button>
          <Button variant="outlined" onClick={handleQuoteFiltersClose}>
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
}
